import { LTableResults } from "@components";
import {
    AssessmentScoreRow,
    convertApiScoreToAssessmentScore,
    getClassFromResultsCode,
    getMoreOrLessText,
    getTableRowsFromAssessmentScores,
    getTableRowsFromTotalLearning
} from "@helpers/scoringHelper";
import { ScoreResult } from "@store/generalTypes";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { Translate, translate } from "react-i18nify";
import { useLanguage } from "../../../../../lang/useLanguage";
import "../styles/results.scss";
import { PerformanceSummary } from "./performanceSummary";

export const PerformanceReport = ({ ...props }: PerformanceReportProps): JSX.Element => {
    const { language } = useLanguage();

    const columnHelper = createColumnHelper<AssessmentScoreRow>();
    const score = useMemo(() => convertApiScoreToAssessmentScore(props.apiScore), [language, props.apiScore]);
    const totalLearningScore = useMemo(() => getTableRowsFromTotalLearning(score), [language, score]);
    const scoringTable = useMemo(() => getTableRowsFromAssessmentScores(score), [language, score]);
    const baseColumns: Array<ColumnDef<AssessmentScoreRow, any>> = useMemo(
        () => [
            columnHelper.accessor("category", {
                header: () => "",
                meta: {
                    cellClassName: "scoring-table-normal scoring-table-category",
                    headerClassName: "has-background-light-blue scoring-table-header scoring-table-category"
                },
                cell: (cell) => <span className="has-text-weight-bold has-text-primary">{cell.getValue()}</span>,
                enableSorting: false
            }),
            columnHelper.accessor("score", {
                header: () => translate("admin.score"),
                cell: (cell) => {
                    return (
                        <span className={getClassFromResultsCode(cell.row.original.result)}>
                            {cell.row.original.score === undefined
                                ? "N/A"
                                : cell.row.original.decimalPlaces === undefined
                                ? cell.row.original.score
                                : cell.row.original.score.toFixed(cell.row.original.decimalPlaces)}
                        </span>
                    );
                },
                meta: {
                    cellClassName: "has-background-ultra-light-blue scoring-table-score",
                    headerClassName: "has-background-light-blue scoring-table-header"
                },
                enableSorting: false
            }),
            columnHelper.accessor("normal", {
                header: () => translate("admin.normal"),
                cell: ({ row }) => {
                    return (
                        <span>
                            {row.original.decimalPlaces === undefined
                                ? row.original.normal
                                : row.original.normal.toFixed(row.original.decimalPlaces)}{" "}
                            {getMoreOrLessText(!row.original.lowerIsBetter)}
                        </span>
                    );
                },
                meta: {
                    cellClassName: "scoring-table-normal",
                    headerClassName: "has-background-light-blue scoring-table-header scoring-table-normal "
                },
                enableSorting: false
            }),
            columnHelper.accessor("marginal", {
                header: () => translate("admin.marginal"),
                meta: {
                    headerClassName: "has-background-light-blue scoring-table-header scoring-table-benchmark-columns"
                },
                enableSorting: false
            }),
            columnHelper.accessor("impaired", {
                header: () => translate("admin.impaired"),
                cell: ({ row }) => {
                    return (
                        <span>
                            {row.original.decimalPlaces === undefined
                                ? row.original.impaired
                                : row.original.impaired.toFixed(row.original.decimalPlaces)}{" "}
                            {getMoreOrLessText(row.original.lowerIsBetter)}
                        </span>
                    );
                },
                meta: {
                    headerClassName: "has-background-light-blue scoring-table-header scoring-table-benchmark-columns"
                },
                enableSorting: false
            })
        ],
        [language, score]
    );
    return (
        <>
            <div className="box is-shadowless">
                <h2 className="title is-5">
                    <Translate value="admin.performanceReport" />
                </h2>
                {LTableResults<AssessmentScoreRow>(totalLearningScore, baseColumns, {
                    hideBorders: true,
                    hidePaging: true,
                    showStriping: true,
                    disableHover: true,
                    isRoundedHeader: true
                })}
                {LTableResults<AssessmentScoreRow>(scoringTable, baseColumns, {
                    hideBorders: true,
                    hidePaging: true,
                    showStriping: true,
                    disableHover: true
                })}
            </div>
            <PerformanceSummary score={score} />
        </>
    );
};

interface PerformanceReportProps {
    apiScore: ScoreResult;
}
